'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { RequestAccessOnMount } from '#pplx/frontend/apps/ask/access/RequestAccessOnMount.js';
import { resolveSearchThreadQueryMode } from '#pplx/frontend/apps/ask/all-results/hooks/allResultsQuery.js';
import { useAllResultsQuery } from '#pplx/frontend/apps/ask/all-results/hooks/useAllResultsQuery.js';
import { isThreadAccessNotAllowedError, useThreadAccessErrorRedirect, } from '#pplx/frontend/apps/ask/all-results/hooks/useThreadAccessErrorRedirect.js';
import { buildThreadLoadEvent, getThreadLoadFailureDecision, getThreadResponseToPaintMs, } from '#pplx/frontend/apps/ask/events/thread/analytics.js';
import { useRearmedThreadLoadEvent } from '#pplx/frontend/apps/ask/events/thread/useRearmedThreadLoadEvent.js';
import { useThreadPostLoadBlocking } from '#pplx/frontend/apps/ask/events/thread/useThreadPostLoadBlocking.js';
import { ThrowFetcherError, HardRedirect, } from '#pplx/frontend/apps/ask/page-components/search/hooks/searchComponents2.js';
import { useResultsActions } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { useSubmitOmniSearchQuery } from '#pplx/frontend/apps/ask/search-ui/useSubmitOmniSearchQuery.js';
import { useHash } from '#pplx/frontend/apps/ask/settings/useSettingsHash.js';
import { isComputerModel, mapPerplexityMessageSearchModeToSearchMode, SearchMode, } from '#pplx/frontend/ask-models/types/search-modes.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { validateUuid } from '#pplx/frontend/common/validateUuid.js';
import { BASE_SUPPORTED_BLOCK_USE_CASES } from '#pplx/frontend/components/utils/consts.js';
import { MessageHelper } from '#pplx/frontend/components/utils/MessageHelper.js';
import { ErrorFallback } from '#pplx/frontend/error-ui/ErrorFallback.js';
import { FetcherError } from '#pplx/frontend/fetcher/errors.js';
import { getTimeOrigin, reportMetric } from '#pplx/frontend/metrics/browser.js';
import { useGetPageTimer, useMarkModuleTTV, } from '#pplx/frontend/metrics/providers/PerformanceProviderShared.js';
import { ErrorBoundary } from '#pplx/frontend/platform-components/ErrorBoundary.js';
import { useParams } from '#pplx/frontend/router/useParams.js';
import { STREAM_ID_PARAM } from '#pplx/frontend/stream/consts.js';
import { createPlaceholderSearchResult } from '#pplx/frontend/stream/createPlaceholderSearchResult.js';
import { useReconnectStream, useStreamStore } from '#pplx/frontend/stream/stream.js';
import { useInitializeThreads } from '#pplx/frontend/stream/useInitializeThreads.js';
import { wasThreadCreatedLocally } from '#pplx/frontend/stream/utils.js';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useRouter as useWouterRouter } from 'wouter';
const THREAD_PAGE_ROUTE = '/search/:slug';
const THREAD_PAGE_MOUNTED = 'web.frontend.thread_page.timing.page_mounted';
const THREAD_QUERY_STARTED = 'web.frontend.thread_page.timing.thread_query_started';
const THREAD_QUERY_RESOLVED = 'web.frontend.thread_page.timing.thread_query_resolved';
const THREAD_QUERY_FAILED = 'web.frontend.thread_page.timing.thread_query_failed';
const THREAD_QUERY_DURATION = 'web.frontend.thread_page.duration.thread_query';
const THREAD_PRELOAD_HEAD_START = 'web.frontend.thread_page.duration.preload_head_start';
const THREAD_PRELOAD_WAIT_AFTER_MOUNT = 'web.frontend.thread_page.duration.preload_wait_after_mount';
const THREAD_RESPONSE_TO_CONTENT_PAINTED = 'web.frontend.thread_page.duration.response_to_content_painted';
function useThreadPageTiming() {
    const getPageTimer = useGetPageTimer();
    return React.useCallback((metricName, extra = {}) => {
        getPageTimer().addTimingOnce(metricName, {
            route: THREAD_PAGE_ROUTE,
            ...extra,
        });
    }, [getPageTimer]);
}
function useThreadPageDurationTiming() {
    const getPageTimer = useGetPageTimer();
    return React.useCallback((metricName, { durationMs, startTime }, extra = {}) => {
        reportMetric(metricName, {
            duration: durationMs,
            is_first_page: getPageTimer().getStartTimeFromTimeOrigin() === 0,
            route: THREAD_PAGE_ROUTE,
            startTime: getTimeOrigin() + startTime,
            ...extra,
        });
    }, [getPageTimer]);
}
export function useThreadPageRenderTiming({ entries, hasContent, isPlaceholderData = false, isPartialLoad = false, isNewThread = false, slugOrUUID, threadQueryDurationMs, threadPreloadStartedAt, threadPreloadResolvedAt, threadQueryResolvedAt, }) {
    const markModuleTTV = useMarkModuleTTV();
    const emitThreadLoadEvent = useRearmedThreadLoadEvent();
    const observePostLoad = useThreadPostLoadBlocking(slugOrUUID);
    const getPageTimer = useGetPageTimer();
    const reportPageTiming = useThreadPageTiming();
    const reportPageDuration = useThreadPageDurationTiming();
    const paintedSlugRef = useRef(undefined);
    const reportedPreloadOverlapSlugRef = useRef(undefined);
    const [pageMounted, setPageMounted] = useState();
    useEffect(() => {
        if (!slugOrUUID)
            return undefined;
        const animationFrameId = window.requestAnimationFrame((mountedAt) => {
            setPageMounted({ at: mountedAt, slugOrUUID });
            reportPageTiming(THREAD_PAGE_MOUNTED);
        });
        return () => window.cancelAnimationFrame(animationFrameId);
    }, [reportPageTiming, slugOrUUID]);
    useEffect(() => {
        if (!slugOrUUID ||
            pageMounted?.slugOrUUID !== slugOrUUID ||
            threadPreloadStartedAt === undefined ||
            threadPreloadResolvedAt === undefined ||
            reportedPreloadOverlapSlugRef.current === slugOrUUID) {
            return;
        }
        reportedPreloadOverlapSlugRef.current = slugOrUUID;
        reportPageDuration(THREAD_PRELOAD_HEAD_START, {
            durationMs: Math.max(0, pageMounted.at - threadPreloadStartedAt),
            startTime: threadPreloadStartedAt,
        });
        reportPageDuration(THREAD_PRELOAD_WAIT_AFTER_MOUNT, {
            durationMs: Math.max(0, threadPreloadResolvedAt - pageMounted.at),
            startTime: pageMounted.at,
        });
    }, [
        pageMounted,
        reportPageDuration,
        slugOrUUID,
        threadPreloadResolvedAt,
        threadPreloadStartedAt,
    ]);
    useEffect(() => {
        if (!slugOrUUID || !hasContent || isPlaceholderData)
            return undefined;
        if (paintedSlugRef.current === slugOrUUID)
            return undefined;
        const animationFrameId = window.requestAnimationFrame(() => {
            paintedSlugRef.current = slugOrUUID;
            const contentPaintedAt = performance.now();
            const pageStartedAtMs = getPageTimer().getStartTimeFromTimeOrigin();
            const responseResolvedAt = threadPreloadResolvedAt ?? threadQueryResolvedAt;
            const responseToPaintMs = getThreadResponseToPaintMs({
                loadStartedAtMs: pageStartedAtMs,
                occurredAtMs: contentPaintedAt,
                responseResolvedAtMs: responseResolvedAt,
            });
            const responseSource = threadPreloadResolvedAt === undefined ? 'network' : 'preload';
            const contentSource = responseResolvedAt === undefined || responseSource === 'preload'
                ? 'cache_or_stream'
                : 'network';
            markModuleTTV('thread-first-content-rendered');
            if (responseResolvedAt !== undefined && responseToPaintMs !== null) {
                reportPageDuration(THREAD_RESPONSE_TO_CONTENT_PAINTED, {
                    durationMs: responseToPaintMs,
                    startTime: responseResolvedAt,
                }, { source: responseSource });
            }
            const load = {
                contentSource,
                contextUUID: entries?.[0]?.context_uuid,
                deduplicationKey: slugOrUUID,
                isPartialLoad,
                isInitialThreadSubmission: isNewThread,
                loadedEntryCount: entries?.length ?? 0,
                loadStartedAtMs: pageStartedAtMs,
                occurredAtMs: contentPaintedAt,
                outcome: 'loaded',
                pageStartedAtMs,
                queryResolvedAtMs: threadQueryResolvedAt,
                queryStartedAtMs: threadQueryResolvedAt !== undefined && threadQueryDurationMs !== undefined
                    ? threadQueryResolvedAt - threadQueryDurationMs
                    : undefined,
                surface: 'ask',
                threadState: entries?.some((entry) => entry.reconnectable && !MessageHelper.isStatusCompleted(entry)) === true
                    ? 'streaming'
                    : 'completed',
                wasDocumentVisible: document.visibilityState === 'visible',
            };
            emitThreadLoadEvent(buildThreadLoadEvent(load));
            observePostLoad(load);
        });
        return () => window.cancelAnimationFrame(animationFrameId);
    }, [
        entries,
        emitThreadLoadEvent,
        observePostLoad,
        getPageTimer,
        hasContent,
        isPlaceholderData,
        isPartialLoad,
        isNewThread,
        markModuleTTV,
        reportPageDuration,
        reportPageTiming,
        slugOrUUID,
        threadQueryDurationMs,
        threadPreloadResolvedAt,
        threadQueryResolvedAt,
    ]);
}
export function useSearchThreadInstanceKey(pathname, frontendUUID, threadUUID) {
    const stateRef = useRef(undefined);
    const previous = stateRef.current;
    // A thread keeps its instance key as long as either identity carries over:
    // the frontend UUID survives the optimistic → real-slug transition, and the
    // thread-level UUID survives latest-first page prepends changing which
    // entry sits at results[0]. Remounting on either would blank the thread and
    // reset scroll state mid-transition. The two are distinct uuid spaces, so
    // only same-slot comparisons are meaningful.
    const hadIdentity = !!previous?.frontendUUID || !!previous?.threadUUID;
    const hasIdentity = !!frontendUUID || !!threadUUID;
    const sharesIdentity = (!!frontendUUID && frontendUUID === previous?.frontendUUID) ||
        (!!threadUUID && threadUUID === previous?.threadUUID);
    const identityChanged = hadIdentity && hasIdentity && !sharesIdentity;
    // A pathname change with no identity yet is a navigation to a different
    // thread (a fresh thread renders before its placeholder identities load).
    // Same-thread pathname changes (optimistic → real slug) keep an identity
    // through the transition, so they don't reset.
    const pathChangedWithoutIdentity = previous != null && !hasIdentity && previous.pathname !== pathname;
    if (!previous || identityChanged || pathChangedWithoutIdentity) {
        stateRef.current = {
            frontendUUID,
            threadUUID,
            key: frontendUUID ?? threadUUID ?? pathname,
            pathname,
        };
    }
    else {
        stateRef.current = {
            ...previous,
            frontendUUID: frontendUUID ?? previous.frontendUUID,
            threadUUID: threadUUID ?? previous.threadUUID,
            pathname,
        };
    }
    return stateRef.current.key;
}
function useNewQuerySearch({ active, query, source, streamId }) {
    const placeholderResults = useMemo(() => {
        return query && active
            ? [
                createPlaceholderSearchResult({
                    query_str: query,
                    frontend_uuid: streamId,
                    query_source: source ?? 'default_search',
                    mode: 'COPILOT',
                }),
            ]
            : EMPTY_ARRAY;
    }, [query, active, streamId, source]);
    return useMemo(() => active
        ? {
            hasFrontendUUID: !!streamId,
            isNewThread: true,
            placeholderResults,
        }
        : undefined, [active, placeholderResults, streamId]);
}
function useNewUUIDSearch({ active, frontendUUID }) {
    const streamStore = useStreamStore();
    const streams = streamStore.getState().streams;
    const stream = active
        ? Object.values(streams).find((stream) => stream.id.description === frontendUUID)
        : undefined;
    const streamResult = stream?.placeholderChunk;
    const placeholderResults = useMemo(() => (streamResult ? [streamResult] : undefined), [streamResult]);
    return useMemo(() => (active ? { isNewThread: true, skipLoadingUI: true, placeholderResults } : undefined), [active, placeholderResults]);
}
function useExistingSearch({ active, slugOrUUID }) {
    const reason = 'search-components';
    const backendUUIDRef = useRef(new Map());
    const locationHash = useHash();
    const reportPageTiming = useThreadPageTiming();
    const reportPageDuration = useThreadPageDurationTiming();
    const getPageTimer = useGetPageTimer();
    const emitThreadLoadEvent = useRearmedThreadLoadEvent();
    const hasRenderedEntriesRef = useRef(undefined);
    const isPlaceholderDataRef = useRef(undefined);
    const successfullyResolvedThreadQueriesRef = useRef(new Set());
    const [threadQueryResolution, setThreadQueryResolution] = useState();
    const threadQueryResolvedAt = threadQueryResolution?.slugOrUUID === slugOrUUID ? threadQueryResolution.resolvedAt : undefined;
    const threadQueryDurationMs = threadQueryResolution?.slugOrUUID === slugOrUUID
        ? threadQueryResolution.queryDurationMs
        : undefined;
    const timing = useMemo(() => active
        ? {
            onThreadQueryFailed: (measurement, failureType) => {
                reportPageTiming(THREAD_QUERY_FAILED, { failure_type: failureType });
                reportPageDuration(THREAD_QUERY_DURATION, measurement, {
                    outcome: 'error',
                });
                const isSameThreadPlaceholder = isPlaceholderDataRef.current?.slug === slugOrUUID &&
                    isPlaceholderDataRef.current.value;
                const hasRenderedEntries = hasRenderedEntriesRef.current?.slug === slugOrUUID &&
                    hasRenderedEntriesRef.current.value;
                const failureDecision = getThreadLoadFailureDecision({
                    hasFirstSuccessResolution: successfullyResolvedThreadQueriesRef.current.has(slugOrUUID),
                    hasRenderedContent: hasRenderedEntries,
                    placeholderMatchesCurrentSlug: isSameThreadPlaceholder,
                    recordedFailureType: failureType,
                });
                if (!failureDecision.shouldEmitFailure)
                    return;
                const pageStartedAtMs = getPageTimer().getStartTimeFromTimeOrigin();
                emitThreadLoadEvent(buildThreadLoadEvent({
                    deduplicationKey: slugOrUUID,
                    failureType: failureDecision.failureType,
                    loadStartedAtMs: pageStartedAtMs,
                    occurredAtMs: performance.now(),
                    outcome: 'failed',
                    pageStartedAtMs,
                    surface: 'ask',
                }));
            },
            onThreadQueryResolved: (measurement) => {
                reportPageTiming(THREAD_QUERY_RESOLVED);
                reportPageDuration(THREAD_QUERY_DURATION, measurement, {
                    outcome: 'success',
                });
                if (successfullyResolvedThreadQueriesRef.current.has(slugOrUUID))
                    return;
                successfullyResolvedThreadQueriesRef.current.add(slugOrUUID);
                setThreadQueryResolution({
                    queryDurationMs: measurement.durationMs,
                    resolvedAt: measurement.startTime + measurement.durationMs,
                    slugOrUUID,
                });
            },
            onThreadQueryStarted: () => reportPageTiming(THREAD_QUERY_STARTED),
        }
        : undefined, [active, emitThreadLoadEvent, getPageTimer, reportPageDuration, reportPageTiming, slugOrUUID]);
    const supportedBlockUseCases = BASE_SUPPORTED_BLOCK_USE_CASES;
    // STEP 1: Determine if the slugOrUUID is a valid in memory stream result.
    const streamStore = useStreamStore();
    const streamStateRef = useRef(streamStore.getState());
    streamStateRef.current = streamStore.getState();
    // From the url slug we will only ever match the first entry in the thread.
    // From the uuid we will get a specific entry--this is the new search case
    // and it will also be the first entry in the thread.
    const streamResult = active
        ? Object.values(streamStateRef.current.entries).find((result) => result?.thread_url_slug === slugOrUUID ||
            result?.backend_uuid === slugOrUUID ||
            result?.backend_uuid === backendUUIDRef.current.get(slugOrUUID))
        : undefined;
    // Cache the backend UUID the first time we find a stream result so that we
    // can lookup the stream store state when we rewrite--the streamResult
    // placeholder won't have a thread url slug on rewrite, and when it does
    // populate it's not going to match the slugOrUUID anymore.
    if (streamResult) {
        backendUUIDRef.current.set(slugOrUUID, streamResult?.backend_uuid ?? '');
    }
    // STEP 2: Check the server if we did not find a stream result. This will
    // update even when disabled, as async stream side-effects write to it.
    // Only disable the query while a stream is actively in progress —
    // completed stream entries stay in the store but should not block server
    // refetches (needed so staleTime-based window-focus refetches can fire).
    const { activeStreams, streams } = streamStateRef.current;
    const isActivelyStreaming = !!streamResult &&
        activeStreams.some((id) => streams[id]?.entryId === streamResult.backend_uuid);
    const enabled = active && !isActivelyStreaming;
    const queryMode = resolveSearchThreadQueryMode(locationHash);
    const { isPartialLoad, threadMetadata, latestEntry, backgroundEntries, pagination, preloadStartedAt, preloadResolvedAt, query: { data: allResults, error, isLoading: _, dataUpdatedAt, isPlaceholderData }, } = useAllResultsQuery({
        slugOrUUID,
        enabled,
        isSidecar: !!(import.meta.env.VITE_IS_SIDECAR_BUILD || import.meta.env.VITE_IS_EMBEDDED_SIDECAR_BUILD),
        reason,
        enablePagination: queryMode.paginationEnabled,
        supportedBlockUseCases,
        // Open at the latest entry and load earlier history on upward scroll intent.
        paginateFromLatest: queryMode.paginateFromLatest,
        timing,
    });
    const entries = useMemo(() => {
        if (allResults?.length)
            return allResults;
        if (streamResult)
            return [streamResult];
        return EMPTY_ARRAY;
    }, [allResults, streamResult]);
    const isNewThread = wasThreadCreatedLocally(Object.values(streams), [
        slugOrUUID,
        streamResult?.backend_uuid,
        streamResult?.context_uuid,
        streamResult?.frontend_context_uuid,
        streamResult?.frontend_uuid,
        streamResult?.thread_url_slug,
    ]);
    isPlaceholderDataRef.current = {
        // `keepPreviousData` belongs to the query that last produced real data,
        // not necessarily the slug currently being fetched.
        slug: isPlaceholderData ? (isPlaceholderDataRef.current?.slug ?? slugOrUUID) : slugOrUUID,
        value: isPlaceholderData,
    };
    if (!isPlaceholderData) {
        hasRenderedEntriesRef.current = { slug: slugOrUUID, value: entries.length > 0 };
    }
    useThreadAccessErrorRedirect({ error, slugOrUUID });
    return useMemo(() => active
        ? {
            entries,
            backgroundEntries,
            slugOrUUID,
            isLoadingEntries: !dataUpdatedAt,
            isPlaceholderData,
            isPartialLoad,
            isNewThread,
            error: error,
            threadMetadata,
            latestEntry,
            pagination,
            threadQueryDurationMs,
            threadPreloadStartedAt: preloadStartedAt,
            threadPreloadResolvedAt: preloadResolvedAt,
            threadQueryResolvedAt,
        }
        : undefined, [
        active,
        entries,
        backgroundEntries,
        error,
        dataUpdatedAt,
        isPlaceholderData,
        isPartialLoad,
        isNewThread,
        slugOrUUID,
        threadMetadata,
        latestEntry,
        pagination,
        threadQueryDurationMs,
        preloadStartedAt,
        preloadResolvedAt,
        threadQueryResolvedAt,
    ]);
}
export function useHandleSearch() {
    const { clear: clearResults } = useResultsActions();
    const params = useParams();
    const newUUIDSearchActive = Boolean(params?.new && params?.uuid);
    const newQuerySearchActive = Boolean(params?.new && !params?.uuid);
    const existingSearchActive = Boolean(!params?.new && params?.uuid);
    const inactive = !newUUIDSearchActive && !newQuerySearchActive && !existingSearchActive;
    // The router's search, like `params`: a page rendered under a nested router
    // (a desktop thread tab in the background) is not on the document's URL.
    // Read raw: wouter's `useSearch` runs `decodeURI` over it, which would
    // decode a query's own escaped `%` twice ("20%30" would become "200").
    const router = useWouterRouter();
    const searchParams = new URLSearchParams(router.searchHook(router));
    const query = searchParams?.get('q') ?? undefined;
    const source = searchParams?.get('source') ?? undefined;
    const streamIdFromParams = searchParams?.get(STREAM_ID_PARAM);
    const streamId = useMemo(() => streamIdFromParams ?? safeRandomUUID(), [streamIdFromParams]);
    if (inactive && !searchParams.has(STREAM_ID_PARAM))
        searchParams.set(STREAM_ID_PARAM, streamId);
    const searchParamsString = inactive ? searchParams.toString() : '';
    const newUUIDProps = useNewUUIDSearch({
        active: newUUIDSearchActive,
        frontendUUID: params?.uuid ?? '',
    });
    const newQueryProps = useNewQuerySearch({
        active: newQuerySearchActive,
        query,
        streamId,
        source,
    });
    const existingProps = useExistingSearch({
        active: existingSearchActive,
        slugOrUUID: params?.uuid ?? '',
    });
    // Clear the state so that we don't see old results when a new search starts
    // useLayoutEffect cleanup runs before the incoming computer thread hydrates cached results.
    // useEffect cleanup runs afterward and clears the newly initialized thread.
    useLayoutEffect(() => () => clearResults(), [clearResults]);
    // Clear the state so that we don't see old results when a new search starts
    // from either a programmatic navigation or comet-navigate event
    // IMPORTANT: The sequence of events is important here.
    // We need to clear the state before the search query is submitted.
    // Otherwise, we'll clear the state after the search query is submitted.
    useLayoutEffect(() => {
        if (newQueryProps?.hasFrontendUUID)
            clearResults();
    }, [clearResults, newQueryProps?.hasFrontendUUID]);
    useSubmitOmniSearchQuery(newQuerySearchActive, streamId, 'new-query-search-components');
    return useMemo(() => {
        if (newUUIDSearchActive) {
            const frontendUUID = params?.uuid;
            if (!frontendUUID || !validateUuid(frontendUUID)) {
                return { component: _jsx(Redirect, { to: "/" }) };
            }
        }
        else if (newQuerySearchActive) {
            if (!query || query === 'pending') {
                return { component: _jsx(Redirect, { to: "/" }) };
            }
        }
        else if (existingSearchActive) {
            const slugOrUUID = params?.uuid;
            if (!slugOrUUID || slugOrUUID === 'undefined') {
                return { component: _jsx(Redirect, { to: "/" }) };
            }
            else if (existingProps?.error instanceof ApiClientsError) {
                if (isThreadAccessNotAllowedError(existingProps.error)) {
                    return {
                        component: _jsx(RequestAccessOnMount, { uuidOrSlug: slugOrUUID, dismissHref: "/" }),
                    };
                }
                // ApiClientsError is typically backend related and we redirect home
                // hook `useEffect` will handle the redirect to base with appropriate params for error message
                return null;
            }
            else if (existingProps?.error instanceof FetcherError && !existingProps.entries.length) {
                // FetcherError is typically network related and outside of our control
                // We show an error fallback if we have no entries to render
                return {
                    component: (_jsx(ErrorBoundary, { fallback: () => _jsx(ErrorFallback, {}), code: "SOMETHING_WENT_WRONG_ERROR", children: _jsx(ThrowFetcherError, { error: existingProps.error }) })),
                };
            }
            else if (existingProps?.entries?.length) {
                // Redirect ASI threads from /search to /computer/tasks
                // Use a full page navigation instead of wouter's <Redirect> because the /search route
                // fetches with BASE_SUPPORTED_BLOCK_USE_CASES (no workflow_steps), and the React Query
                // cache key doesn't include supportedBlockUseCases, so a client-side redirect would
                // serve stale cached data missing workflow blocks.
                const firstEntry = existingProps.entries[0];
                const searchMode = mapPerplexityMessageSearchModeToSearchMode(firstEntry?.search_mode);
                if (searchMode === SearchMode.ASI || isComputerModel(firstEntry?.display_model)) {
                    return {
                        component: (_jsx(HardRedirect, { to: `/computer/tasks/${slugOrUUID}${window.location.search}` })),
                    };
                }
            }
        }
        else {
            return {
                component: _jsx(Redirect, { to: query ? `/search/new?${searchParamsString}` : '/' }),
            };
        }
        return {
            props: {
                ...newUUIDProps,
                ...newQueryProps,
                ...existingProps,
            },
        };
    }, [
        existingProps,
        existingSearchActive,
        newQueryProps,
        newQuerySearchActive,
        newUUIDProps,
        newUUIDSearchActive,
        params?.uuid,
        query,
        searchParamsString,
    ]);
}
export function useHandleSearchThread(props) {
    const initializeThreads = useInitializeThreads();
    const { setAuthoritativeLastResult, setCurrentThreadId, setThreadMetadata } = useResultsActions();
    const reconnectStream = useReconnectStream();
    const entries = props.entries;
    const hasEntries = !!entries?.length;
    const isLoadingEntries = props.isLoadingEntries;
    const threadId = useMemo(() => entries?.[0]?.context_uuid, [entries]);
    useEffect(() => {
        // `setCurrentThreadId` has a side-effect to read in the stream store;
        // no need to keep doing it after the initial mount
        if (hasEntries)
            setCurrentThreadId(threadId ?? safeRandomUUID());
    }, [hasEntries, setCurrentThreadId, threadId]);
    useEffect(() => {
        if (props.threadMetadata)
            setThreadMetadata(props.threadMetadata);
    }, [props.threadMetadata, setThreadMetadata]);
    useEffect(() => {
        setAuthoritativeLastResult(props.pagination ? (props.latestEntry ?? undefined) : undefined);
    }, [props.latestEntry, props.pagination, setAuthoritativeLastResult]);
    const backgroundEntries = props.backgroundEntries;
    useEffect(() => {
        if (isLoadingEntries)
            return;
        if (entries) {
            const allEntries = backgroundEntries?.length ? [...entries, ...backgroundEntries] : entries;
            const reconnectableEntries = props.latestEntry
                ? [...allEntries, props.latestEntry]
                : allEntries;
            reconnectableEntries.forEach((entry) => {
                if (entry.reconnectable && !MessageHelper.isStatusCompleted(entry)) {
                    reconnectStream(entry.backend_uuid, {
                        contextUuid: entry.context_uuid,
                        frontendContextUuid: entry.frontend_context_uuid,
                    }); // noop if already streaming
                }
            });
            if (props.pagination) {
                initializeThreads(allEntries, { synchronizeEntryOrder: true });
            }
            else {
                initializeThreads(allEntries); // noop if thread entry is already initialized
            }
        }
        return;
    }, [
        initializeThreads,
        entries,
        backgroundEntries,
        reconnectStream,
        threadId,
        isLoadingEntries,
        props.pagination,
        props.latestEntry,
    ]);
    const placeholderResults = useMemo(() => {
        if (props.entries?.length) {
            return props.entries;
        }
        if (props.placeholderResults?.length) {
            return props.placeholderResults;
        }
        return EMPTY_ARRAY;
    }, [props.placeholderResults, props.entries]);
    return useMemo(() => ({ placeholderResults }), [placeholderResults]);
}
//# sourceMappingURL=search.js.map